import {
  isOverlappingOrDuplicateTranscript,
  normalizeSpeechTranscriptForDedupe,
} from '@/shared/lib/speechTranscriptDedupe'
import type { AnamnesisTranscriptionProvider } from './anamnesisTranscriptionEngine'

export type TranscriptChunkKind = 'interim' | 'final'

export type TranscriptSwitchReason =
  | 'provider-switch'
  | 'provider-fallback'
  | 'provider-retry'
  | 'provider-failure'

export interface AnamnesisTranscriptTechnicalMetadata {
  provider?: AnamnesisTranscriptionProvider
  engine?: AnamnesisTranscriptionProvider
  sourceProvider?: AnamnesisTranscriptionProvider
  chunkId?: string
  sequence?: number
  confidence?: number
  switchId?: string
  engineAttempt?: number
  signature?: string
}

export interface TranscriptReconciliationChunk extends AnamnesisTranscriptTechnicalMetadata {
  text: string
  kind: TranscriptChunkKind
  timestamp?: Date | string | number
}

export interface TranscriptReconciliationSnapshot<TBlock = unknown> {
  interimText: string
  confirmedText: string
  semanticBlocks: TBlock[]
  metadata?: AnamnesisTranscriptTechnicalMetadata
}

export interface TranscriptProviderSwitch {
  fromProvider?: AnamnesisTranscriptionProvider
  toProvider: AnamnesisTranscriptionProvider
  reason: TranscriptSwitchReason
  switchId?: string
  engineAttempt?: number
}

export interface ReconciledTranscriptChunk {
  text: string
  kind: TranscriptChunkKind
  metadata: AnamnesisTranscriptTechnicalMetadata
  duplicate: boolean
}

const SAFE_TECHNICAL_ID_PATTERN = /^[a-z0-9._:-]+$/i

const sanitizeTechnicalId = (value?: string): string | undefined => {
  const cleanValue = value?.trim()
  if (!cleanValue || cleanValue.length > 96 || !SAFE_TECHNICAL_ID_PATTERN.test(cleanValue)) {
    return undefined
  }
  return cleanValue
}

const sanitizeSequence = (value?: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.trunc(value) : undefined

const sanitizeConfidence = (value?: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : undefined

export const sanitizeTranscriptTechnicalMetadata = (
  metadata: AnamnesisTranscriptTechnicalMetadata = {},
): AnamnesisTranscriptTechnicalMetadata => {
  const sanitized: AnamnesisTranscriptTechnicalMetadata = {}
  if (metadata.provider) sanitized.provider = metadata.provider
  if (metadata.engine) sanitized.engine = metadata.engine
  if (metadata.sourceProvider) sanitized.sourceProvider = metadata.sourceProvider

  const chunkId = sanitizeTechnicalId(metadata.chunkId)
  const switchId = sanitizeTechnicalId(metadata.switchId)
  const signature = sanitizeTechnicalId(metadata.signature)
  const sequence = sanitizeSequence(metadata.sequence)
  const engineAttempt = sanitizeSequence(metadata.engineAttempt)
  const confidence = sanitizeConfidence(metadata.confidence)

  if (chunkId) sanitized.chunkId = chunkId
  if (switchId) sanitized.switchId = switchId
  if (signature) sanitized.signature = signature
  if (sequence !== undefined) sanitized.sequence = sequence
  if (engineAttempt !== undefined) sanitized.engineAttempt = engineAttempt
  if (confidence !== undefined) sanitized.confidence = confidence

  return sanitized
}

const toTimestampMs = (timestamp?: Date | string | number): number => {
  if (timestamp instanceof Date) return timestamp.getTime()
  if (typeof timestamp === 'number') return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER
  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp)
    return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed
  }
  return Number.MAX_SAFE_INTEGER
}

export const sortTranscriptChunks = <T extends TranscriptReconciliationChunk>(chunks: T[]): T[] =>
  [...chunks].sort((left, right) => {
    const leftSequence = sanitizeSequence(left.sequence)
    const rightSequence = sanitizeSequence(right.sequence)
    if (leftSequence !== undefined && rightSequence !== undefined && leftSequence !== rightSequence) {
      return leftSequence - rightSequence
    }
    if (leftSequence !== undefined && rightSequence === undefined) return -1
    if (leftSequence === undefined && rightSequence !== undefined) return 1
    return toTimestampMs(left.timestamp) - toTimestampMs(right.timestamp)
  })

export const buildTranscriptChunkSignature = (chunk: TranscriptReconciliationChunk): string => {
  const normalizedText = normalizeSpeechTranscriptForDedupe(chunk.text)
  const provider = chunk.provider ?? chunk.sourceProvider ?? 'none'
  const engine = chunk.engine ?? provider
  const attempt = sanitizeSequence(chunk.engineAttempt) ?? 0
  const sequence = sanitizeSequence(chunk.sequence)
  const chunkId = sanitizeTechnicalId(chunk.chunkId)

  if (chunkId) return `id:${provider}:${engine}:${attempt}:${chunkId}`
  if (sequence !== undefined) return `seq:${provider}:${engine}:${attempt}:${sequence}:${normalizedText}`
  return `text:${provider}:${engine}:${attempt}:${normalizedText}`
}

export class TranscriptReconciliationEngine {
  private readonly seenSignatures = new Set<string>()

  private readonly confirmedTexts: string[] = []

  private activeProvider?: AnamnesisTranscriptionProvider

  private switchCounter = 0

  reconcileChunk(chunk: TranscriptReconciliationChunk): ReconciledTranscriptChunk | null {
    const cleanText = chunk.text.trim()
    if (!cleanText) return null

    const signature = buildTranscriptChunkSignature({ ...chunk, text: cleanText })
    const metadata = sanitizeTranscriptTechnicalMetadata({
      provider: chunk.provider,
      engine: chunk.engine ?? chunk.provider,
      sourceProvider: chunk.sourceProvider ?? chunk.provider,
      chunkId: chunk.chunkId,
      sequence: chunk.sequence,
      confidence: chunk.confidence,
      switchId: chunk.switchId,
      engineAttempt: chunk.engineAttempt,
      signature,
    })

    if (this.seenSignatures.has(signature)) {
      return { text: cleanText, kind: chunk.kind, metadata, duplicate: true }
    }

    if (chunk.kind === 'final' && this.confirmedTexts.some((text) => isOverlappingOrDuplicateTranscript(text, cleanText))) {
      this.seenSignatures.add(signature)
      return { text: cleanText, kind: chunk.kind, metadata, duplicate: true }
    }

    this.seenSignatures.add(signature)
    if (chunk.kind === 'final') {
      this.confirmedTexts.push(cleanText)
    }
    if (chunk.provider) {
      this.activeProvider = chunk.provider
    }

    return { text: cleanText, kind: chunk.kind, metadata, duplicate: false }
  }

  reconcileChunks(chunks: TranscriptReconciliationChunk[]): ReconciledTranscriptChunk[] {
    return sortTranscriptChunks(chunks)
      .map((chunk) => this.reconcileChunk(chunk))
      .filter((chunk): chunk is ReconciledTranscriptChunk => Boolean(chunk))
  }

  recordProviderSwitch<TBlock>(
    snapshot: TranscriptReconciliationSnapshot<TBlock>,
    switchEvent: TranscriptProviderSwitch,
  ): TranscriptReconciliationSnapshot<TBlock> {
    const switchId =
      sanitizeTechnicalId(switchEvent.switchId) ??
      `switch-${Date.now().toString(36)}-${(++this.switchCounter).toString(36)}`
    this.activeProvider = switchEvent.toProvider

    return {
      interimText: snapshot.interimText,
      confirmedText: snapshot.confirmedText,
      semanticBlocks: snapshot.semanticBlocks,
      metadata: sanitizeTranscriptTechnicalMetadata({
        provider: switchEvent.toProvider,
        engine: switchEvent.toProvider,
        sourceProvider: switchEvent.fromProvider ?? this.activeProvider,
        switchId,
        engineAttempt: switchEvent.engineAttempt,
      }),
    }
  }
}
